import { useMemo } from "react";
import * as THREE from "three";
import type { LaptopPropSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/** A muted "live dashboard" drawn on the laptop screen — atmosphere only, no
 *  interaction. Dark theme so it reads as "on" without competing for
 *  attention with the room. */
function makeLaptopScreenTexture(label: string): THREE.CanvasTexture {
  const w = 1024;
  const h = 640;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#101a2c");
  grad.addColorStop(1, "#0a1220");
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // header bar
  g.fillStyle = "rgba(255,255,255,0.06)";
  g.fillRect(0, 0, w, 64);
  g.fillStyle = "#ffb020";
  g.font = "700 26px Segoe UI, Arial, sans-serif";
  g.textAlign = "left";
  g.fillText(label, 32, 42);
  ["#ff6b6b", "#ffd166", "#8fe3a0"].forEach((c, i) => {
    g.fillStyle = c;
    g.beginPath();
    g.arc(w - 40 - i * 26, 32, 7, 0, Math.PI * 2);
    g.fill();
  });
  // scoreboard rows
  const rows = [
    ["IRONBRIDGE UTD", "2", "1", "HARBOR CITY"],
    ["STEEL VALLEY", "3", "0", "MILLPORT"],
    ["NORTHGATE ROV", "1", "1", "RIVERSIDE ATH"],
  ];
  rows.forEach((r, i) => {
    const y = 120 + i * 74;
    g.fillStyle = i % 2 === 0 ? "rgba(255,255,255,0.045)" : "rgba(255,255,255,0.02)";
    g.fillRect(32, y - 30, w - 64, 56);
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.font = "500 24px Segoe UI, Arial, sans-serif";
    g.textAlign = "left";
    g.fillText(r[0], 56, y + 8);
    g.textAlign = "right";
    g.fillText(r[3], w - 56, y + 8);
    g.textAlign = "center";
    g.fillStyle = "#ffb020";
    g.font = "700 26px Segoe UI, Arial, sans-serif";
    g.fillText(`${r[1]} - ${r[2]}`, w / 2, y + 9);
  });
  // small line-chart squiggle (footer widget)
  g.strokeStyle = "#8fe3a0";
  g.lineWidth = 3;
  g.beginPath();
  const pts = [0.12, 0.22, 0.18, 0.32, 0.28, 0.44, 0.5, 0.42, 0.62, 0.7, 0.6, 0.78, 0.86];
  pts.forEach((v, i) => {
    const x = 56 + (i / (pts.length - 1)) * (w - 112);
    const y = 560 - v * 100;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  });
  g.stroke();
  g.fillStyle = "rgba(255,255,255,0.35)";
  g.font = "16px Segoe UI, Arial, sans-serif";
  g.textAlign = "left";
  g.fillText("VIEWERSHIP · LIVE", 56, 500);

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** A tidy grid of rounded keys + a trackpad, baked as one texture for the
 *  keyboard deck — cheap to render, reads fine at prop distance. */
function makeKeyboardTexture(): THREE.CanvasTexture {
  const w = 1024;
  const h = 700;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#c9ccd0";
  g.fillRect(0, 0, w, h);
  const cols = 14;
  const rows = 5;
  const pad = 44;
  const gap = 8;
  const cellW = (w - pad * 2 - gap * (cols - 1)) / cols;
  const cellH = 62;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = pad + c * (cellW + gap);
      const y = 40 + r * (cellH + gap);
      g.fillStyle = "#e7e9ec";
      g.beginPath();
      g.roundRect(x, y, cellW, cellH, 6);
      g.fill();
      g.strokeStyle = "rgba(0,0,0,0.08)";
      g.lineWidth = 1.5;
      g.stroke();
    }
  }
  // trackpad
  g.fillStyle = "#bfc3c8";
  g.beginPath();
  g.roundRect(w / 2 - 160, h - 150, 320, 110, 10);
  g.fill();
  g.strokeStyle = "rgba(0,0,0,0.12)";
  g.stroke();
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/**
 * A procedural open laptop prop (base + keyboard deck + trackpad + hinged
 * screen lid with a soft-glowing dashboard) — pure atmosphere on a bar
 * counter/desk, matching the quality bar of DeskWorkstation/RoomKit props
 * (no external assets, PBR primitives + baked canvas textures).
 */
export function OpenLaptop({ spec }: { spec: LaptopPropSpec }) {
  const scale = spec.scale ?? 1;
  const yaw = (spec.rotationDeg ?? 0) * DEG;
  const screenTex = useMemo(() => makeLaptopScreenTexture(spec.screenLabel ?? "LIVE SCORES"), [spec.screenLabel]);
  const kbTex = useMemo(() => makeKeyboardTexture(), []);

  const shell = "#3a3d42";
  const shellDark = "#26282c";

  // Base footprint ~32cm x 22cm, ~1.4cm thick (a real 13-14" laptop); lid
  // (screen + bezel) ~21cm tall, hinged open ~112deg from the base plane.
  // Everything is local to a group whose origin sits ON the surface the
  // laptop rests on (position = that point).
  const BASE_W = 0.32;
  const BASE_D = 0.22;
  const BASE_H = 0.014;
  const LID_H = 0.21;
  const LID_T = 0.012;
  const SCREEN_OPEN_DEG = 112;

  return (
    <group position={spec.position} rotation={[0, yaw, 0]} scale={scale}>
      {/* base shell */}
      <mesh position={[0, BASE_H / 2, 0]} castShadow>
        <boxGeometry args={[BASE_W, BASE_H, BASE_D]} />
        <meshStandardMaterial color={shell} metalness={0.65} roughness={0.3} />
      </mesh>
      {/* keyboard deck (slightly recessed, textured) */}
      <mesh position={[0, BASE_H + 0.0011, 0.005]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[BASE_W * 0.92, BASE_D * 0.86]} />
        <meshStandardMaterial map={kbTex} metalness={0.1} roughness={0.65} />
      </mesh>
      {/* front lip edge (slight taper reads as a real laptop, not a slab) */}
      <mesh position={[0, BASE_H * 0.4, BASE_D / 2 + 0.001]}>
        <boxGeometry args={[BASE_W * 0.98, BASE_H * 0.7, 0.004]} />
        <meshStandardMaterial color={shellDark} metalness={0.5} roughness={0.4} />
      </mesh>

      {/* hinge + screen lid, pivoting at the back edge of the base. Local
          +Y (before the recline tilt below) is "lid fully upright". */}
      <group position={[0, BASE_H, -BASE_D / 2]} rotation={[-(SCREEN_OPEN_DEG * DEG - Math.PI / 2), 0, 0]}>
        {/* hinge bar */}
        <mesh position={[0, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.006, 0.006, BASE_W * 0.94, 12]} />
          <meshStandardMaterial color={shellDark} metalness={0.7} roughness={0.3} />
        </mesh>
        {/* lid body (back of screen) */}
        <mesh position={[0, LID_H / 2, -0.006]}>
          <boxGeometry args={[BASE_W, LID_H, LID_T]} />
          <meshStandardMaterial color={shell} metalness={0.65} roughness={0.3} />
        </mesh>
        {/* screen bezel */}
        <mesh position={[0, LID_H / 2, 0.0005]}>
          <boxGeometry args={[BASE_W * 0.96, LID_H * 0.96, 0.006]} />
          <meshStandardMaterial color="#0c0e11" metalness={0.3} roughness={0.6} />
        </mesh>
        {/* emissive screen surface */}
        <mesh position={[0, LID_H / 2, 0.0045]}>
          <planeGeometry args={[BASE_W * 0.88, LID_H * 0.86]} />
          <meshStandardMaterial
            map={screenTex}
            emissive="#ffffff"
            emissiveMap={screenTex}
            emissiveIntensity={0.65}
            toneMapped={false}
          />
        </mesh>
        {/* webcam dot */}
        <mesh position={[0, LID_H - 0.006, 0.0048]}>
          <circleGeometry args={[0.0025, 12]} />
          <meshStandardMaterial color="#0a0a0a" />
        </mesh>
      </group>
    </group>
  );
}
